import { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { Award, BookOpen, Flame, Lock, Sparkles, Star, Trophy, type LucideIcon } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { BadgeMedallion } from '@/components/super-admin/badges/BadgeMedallion'
import '@/components/super-admin/badges/styles.css'
import { formatBrDate } from '@/components/super-admin/badges/format'
import type { Badge, CriterionType } from '@/components/super-admin/badges/types'
import { useMyBadges, useVisibleBadges, type UserBadge } from '@/services/badges.service'
import { cn } from '@/lib/utils'

// =============================================================== //
// Mapas — categoria derivada do `criterion.type`                  //
// =============================================================== //
//
// Mantém a UX antiga (4 categorias humanas) sem espalhar dado novo no
// schema. Quando a equipe de produto decidir uma 5ª categoria, basta
// expandir o mapa e o tipo TS força adicionar todos os criterion.type.

type Category = 'Aprendizado' | 'Consistência' | 'Prática' | 'Especial'

const CATEGORY_MAP: Record<CriterionType, Category> = {
  account_created: 'Especial',
  any_activity: 'Especial',
  specific_episode: 'Aprendizado',
  specific_module: 'Aprendizado',
  specific_course: 'Aprendizado',
  count_episodes: 'Aprendizado',
  count_modules: 'Aprendizado',
  count_courses: 'Aprendizado',
  streak_days: 'Consistência',
  day_marathon: 'Consistência',
  return_after_idle: 'Consistência',
  quiz_perfect: 'Prática',
  quiz_perfect_streak: 'Prática',
  module_all_quizzes_perfect: 'Prática',
  quiz_comeback: 'Prática',
  manual_roster: 'Especial',
}

const CATEGORY_ICONS: Record<Category, LucideIcon> = {
  Aprendizado: BookOpen,
  Consistência: Flame,
  Prática: Sparkles,
  Especial: Star,
}

// Cor base de cada categoria — usada no header da seção e no glow
// das conquistadas. Mantém alinhamento com a paleta dark + laranja.
const CATEGORY_COLORS: Record<Category, { text: string; ring: string }> = {
  Aprendizado: { text: 'text-sky-400', ring: 'shadow-sky-500/30' },
  Consistência: { text: 'text-orange-400', ring: 'shadow-orange-500/40' },
  Prática: { text: 'text-fuchsia-400', ring: 'shadow-fuchsia-500/30' },
  Especial: { text: 'text-amber-400', ring: 'shadow-amber-400/40' },
}

const CATEGORY_ORDER: Category[] = ['Especial', 'Aprendizado', 'Consistência', 'Prática']

// =============================================================== //
// Cruzamento dos 2 endpoints                                      //
// =============================================================== //

interface BadgeRow {
  badge: Badge
  category: Category
  earned: boolean
  earnedAt: string | null
}

function combineBadges(visible: Badge[], earned: UserBadge[]): BadgeRow[] {
  // Map por id pra lookup O(1)
  const earnedById = new Map(earned.map((e) => [e.badge.id, e]))

  // Inclui também badges conquistadas que sumiram do catálogo visível
  // (ex.: admin desativou ou mudou de empresa) — mantém histórico.
  const visibleIds = new Set(visible.map((b) => b.id))
  const orphanEarned = earned.filter((e) => !visibleIds.has(e.badge.id)).map((e) => e.badge)

  return [...visible, ...orphanEarned].map((badge) => {
    const userBadge = earnedById.get(badge.id)
    return {
      badge,
      category: CATEGORY_MAP[badge.criterion.type],
      earned: !!userBadge,
      earnedAt: userBadge?.earnedAt ?? null,
    }
  })
}

type FilterMode = 'all' | 'earned' | 'locked'

// =============================================================== //
// Subcomponentes                                                   //
// =============================================================== //

function BadgeItem({ row, color }: { row: BadgeRow; color: { ring: string } }) {
  const { badge, earned, earnedAt } = row

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <motion.div
          whileHover={{ y: -3, scale: 1.04 }}
          transition={{ type: 'spring', stiffness: 300, damping: 18 }}
          className="flex flex-col items-center gap-2 cursor-default select-none"
        >
          <div
            className={cn(
              'relative transition-all duration-300',
              // Sem iconUrl: BadgeMedallion renderiza a bola circular →
              // `rounded-full` no glow combina. Com iconUrl: PNG pode
              // ter forma livre (estrela, bandeira, batuta...) → uso
              // `rounded-2xl` no glow pra acomodar sem cortar.
              earned && badge.iconUrl ? 'rounded-2xl' : 'rounded-full',
              earned ? `shadow-lg ${color.ring}` : 'opacity-30 grayscale'
            )}
          >
            <BadgeMedallion
              tier={badge.tier}
              iconUrl={earned ? badge.iconUrl : null}
              inscription={badge.name}
              size={92}
              // Bloqueada: nome já aparece embaixo → sem texto no círculo.
              showInscription={earned}
            />
            {!earned && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full">
                <Lock className="w-6 h-6 text-white/70" />
              </div>
            )}
          </div>
          <div className="text-center min-h-[2.5rem]">
            <p
              className={cn(
                'text-xs font-semibold leading-tight line-clamp-2 max-w-[7rem]',
                earned ? 'text-foreground' : 'text-muted-foreground'
              )}
            >
              {badge.name}
            </p>
            {earned && earnedAt && (
              <p className="text-[10px] text-muted-foreground mt-0.5">{formatBrDate(earnedAt)}</p>
            )}
          </div>
        </motion.div>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[220px] text-center">
        <p className="font-semibold text-sm">{badge.name}</p>
        {earned ? (
          <>
            {/* Conquistada: description é o "parabéns / contexto" da
                badge (ex.: "Você foi um dos primeiros a se cadastrar"). */}
            <p className="text-[11px] text-muted-foreground mt-0.5">{badge.description}</p>
            {earnedAt && (
              <p className="text-[11px] text-primary mt-1.5">
                Conquistada em {formatBrDate(earnedAt)}
              </p>
            )}
          </>
        ) : (
          /* Bloqueada: NÃO mostra description ("parabéns" antes de
             conquistar é estranho). Só o howToEarn pra orientar. */
          <p className="text-[11px] text-muted-foreground mt-0.5 italic">{badge.howToEarn}</p>
        )}
      </TooltipContent>
    </Tooltip>
  )
}

function CategorySection({ category, rows }: { category: Category; rows: BadgeRow[] }) {
  const Icon = CATEGORY_ICONS[category]
  const color = CATEGORY_COLORS[category]
  const earnedCount = rows.filter((r) => r.earned).length

  return (
    <section>
      <header className="flex items-center justify-between mb-4">
        <div className={cn('flex items-center gap-2 font-semibold', color.text)}>
          <Icon className="w-4 h-4" />
          <span className="text-sm uppercase tracking-wide">{category}</span>
        </div>
        <span className="text-[11px] text-muted-foreground tabular-nums">
          {earnedCount}/{rows.length}
        </span>
      </header>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-y-5 gap-x-3">
        {rows.map((row) => (
          <BadgeItem key={row.badge.id} row={row} color={color} />
        ))}
      </div>
    </section>
  )
}

function BadgesSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-3 w-full rounded-full" />
      {[0, 1, 2].map((i) => (
        <div key={i} className="space-y-3">
          <Skeleton className="h-4 w-32" />
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
            {[0, 1, 2, 3, 4].map((j) => (
              <div key={j} className="flex flex-col items-center gap-2">
                <Skeleton className="w-[92px] h-[92px] rounded-full" />
                <Skeleton className="h-3 w-16" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center text-center py-10 px-4">
      <div className="rounded-full bg-muted/30 p-4 mb-3">
        <Trophy className="w-8 h-8 text-muted-foreground" />
      </div>
      <p className="font-semibold text-foreground mb-1">Nenhuma badge disponível ainda</p>
      <p className="text-sm text-muted-foreground max-w-sm">
        Quando novas conquistas forem cadastradas, elas aparecerão por aqui.
      </p>
    </div>
  )
}

// =============================================================== //
// Componente principal                                             //
// =============================================================== //

export default function BadgesCard() {
  const [filter, setFilter] = useState<FilterMode>('all')

  const myBadgesQuery = useMyBadges()
  const visibleBadgesQuery = useVisibleBadges()

  const isLoading = myBadgesQuery.isLoading || visibleBadgesQuery.isLoading
  const hasError = myBadgesQuery.isError || visibleBadgesQuery.isError

  const rows = useMemo<BadgeRow[]>(() => {
    if (!myBadgesQuery.data || !visibleBadgesQuery.data) return []
    // Conquistadas primeiro dentro de cada categoria. O sort é estável,
    // então a ordem original (por `order` da badge) é preservada dentro
    // de cada grupo earned/locked.
    return combineBadges(visibleBadgesQuery.data, myBadgesQuery.data).sort(
      (a, b) => Number(b.earned) - Number(a.earned)
    )
  }, [myBadgesQuery.data, visibleBadgesQuery.data])

  const earnedCount = rows.filter((r) => r.earned).length
  const totalCount = rows.length
  const progressPct = totalCount > 0 ? (earnedCount / totalCount) * 100 : 0
  const totalPoints = rows.filter((r) => r.earned).reduce((sum, r) => sum + r.badge.points, 0)

  // Filtro aplicado antes do agrupamento por categoria
  const filteredRows = rows.filter((r) => {
    if (filter === 'earned') return r.earned
    if (filter === 'locked') return !r.earned
    return true
  })

  const grouped = useMemo(() => {
    const map = new Map<Category, BadgeRow[]>()
    for (const row of filteredRows) {
      const list = map.get(row.category) ?? []
      list.push(row)
      map.set(row.category, list)
    }
    return map
  }, [filteredRows])

  return (
    <Card className="glass-card border-border/50 overflow-hidden">
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-primary" />
              Conquistas
            </CardTitle>
            <CardDescription className="mt-1">
              {isLoading ? (
                'Carregando suas badges...'
              ) : (
                <>
                  <span className="text-foreground font-semibold tabular-nums">{earnedCount}</span>{' '}
                  de <span className="tabular-nums">{totalCount}</span> desbloqueadas
                  {totalPoints > 0 && (
                    <>
                      {' · '}
                      <span className="text-primary font-semibold tabular-nums">
                        {totalPoints}
                      </span>{' '}
                      pts
                    </>
                  )}
                </>
              )}
            </CardDescription>
          </div>
          {!isLoading && totalCount > 0 && (
            <ToggleGroup
              type="single"
              size="sm"
              value={filter}
              onValueChange={(v) => v && setFilter(v as FilterMode)}
              className="hidden sm:flex"
            >
              <ToggleGroupItem value="all" className="text-xs h-7 px-2.5">
                Todas
              </ToggleGroupItem>
              <ToggleGroupItem value="earned" className="text-xs h-7 px-2.5">
                Conquistadas
              </ToggleGroupItem>
              <ToggleGroupItem value="locked" className="text-xs h-7 px-2.5">
                Bloqueadas
              </ToggleGroupItem>
            </ToggleGroup>
          )}
        </div>

        {!isLoading && totalCount > 0 && (
          <div className="mt-4">
            <div className="h-2 bg-muted/40 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progressPct}%` }}
                transition={{ duration: 0.9, ease: 'easeOut' }}
                className="h-full bg-gradient-to-r from-primary via-amber-400 to-primary rounded-full"
              />
            </div>
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-7">
        {isLoading ? (
          <BadgesSkeleton />
        ) : hasError ? (
          <div className="text-sm text-muted-foreground text-center py-8">
            Não foi possível carregar as conquistas. Tente recarregar a página.
          </div>
        ) : totalCount === 0 ? (
          <EmptyState />
        ) : filteredRows.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            {filter === 'earned'
              ? 'Você ainda não conquistou nenhuma badge. Continue assistindo, resolvendo quizzes e estudando!'
              : 'Você já conquistou todas as badges desta categoria. Impressionante! 🎉'}
          </p>
        ) : (
          <TooltipProvider delayDuration={150}>
            {CATEGORY_ORDER.map((category) => {
              const list = grouped.get(category)
              if (!list || list.length === 0) return null
              return <CategorySection key={category} category={category} rows={list} />
            })}
          </TooltipProvider>
        )}
      </CardContent>
    </Card>
  )
}
